import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Paper, Alert, CircularProgress } from '@mui/material';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, setDoc } from 'firebase/firestore';
import { ROLES } from '../../firebase.marketplace';

/**
 * Simple component to display user ID and make current user an admin
 */
const SimpleAdminSetup = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      
      // Log user ID to console immediately when user is available
      if (currentUser) {
        console.log('CURRENT USER ID:', currentUser.uid);
        console.log('CURRENT USER EMAIL:', currentUser.email);
      }
    });

    return () => unsubscribe();
  }, []);

  const makeAdmin = async () => {
    if (!user) {
      setError('You must be logged in to perform this action');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const db = getFirestore();
      
      // Create or update user document with admin roles
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || user.email.split('@')[0],
        photoURL: user.photoURL || null,
        roles: {
          [ROLES.USER]: true,
          [ROLES.ADMIN]: true,
          [ROLES.ARTIST]: true,
          superadmin: true
        },
        updatedAt: new Date()
      }, { merge: true });
      
      setSuccess(true);
      
      // Log success to console
      console.log('Successfully made user an admin!');
      console.log('User ID:', user.uid);
      console.log('User email:', user.email);
      
    } catch (err) {
      console.error('Error making admin:', err);
      setError(err.message || 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom align="center">
          Simple Admin Setup
        </Typography>
        
        {user ? (
          <>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body1">
                <strong>Your User ID has been logged to the console.</strong>
              </Typography>
              <Typography variant="body2">
                Press F12 to open the developer console and look for "CURRENT USER ID".
              </Typography>
            </Alert>
            
            <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mb: 3 }}>
              <Typography variant="body1">
                <strong>User ID:</strong> {user.uid}
              </Typography>
              <Typography variant="body1">
                <strong>Email:</strong> {user.email}
              </Typography>
            </Box>
            
            <Button 
              variant="contained" 
              color="primary"
              fullWidth
              disabled={loading || success}
              onClick={makeAdmin}
            >
              {loading ? <CircularProgress size={24} /> : 'Make Me Admin'}
            </Button>
            
            {success && (
              <Alert severity="success" sx={{ mt: 3 }}>
                Successfully made {user.email} an admin!
              </Alert>
            )}
            
            {error && (
              <Alert severity="error" sx={{ mt: 3 }}>
                {error}
              </Alert>
            )}
          </>
        ) : (
          <Alert severity="warning">
            You must be logged in to view your User ID and perform admin setup.
            Please sign in first.
          </Alert>
        )}
      </Paper>
      
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Instructions
        </Typography>
        
        <ol>
          <li>Sign in to your account</li>
          <li>Your User ID will be displayed above and logged to the console</li>
          <li>Click "Make Me Admin" to grant yourself admin privileges</li>
          <li>After success, you can access the admin panel at <code>/marketplace/admin/tools</code></li>
        </ol>
        
        <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
          If you're still having trouble, try the Create Admin option at <code>/marketplace/create-admin</code>
        </Typography>
      </Paper>
    </Box>
  );
};

export default SimpleAdminSetup;
